// Helper function to hide savedAt tooltip
export function hideSavedAtTooltip(
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  setShowSavedAtTooltip(false)
  setSavedAtTooltipPos(null)
}

// Helper function to check if click is outside savedAt tooltip
function isClickOutsideSavedAtTooltip(
  event: MouseEvent,
  savedAtRef: React.RefObject<HTMLSpanElement>
): boolean {
  return !!(savedAtRef.current && !savedAtRef.current.contains(event.target as Node))
}

// Helper function to create scroll handler
function createScrollHandler(
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  return () => hideSavedAtTooltip(setShowSavedAtTooltip, setSavedAtTooltipPos)
}

// Helper function to create blur handler
function createBlurHandler(
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  return () => hideSavedAtTooltip(setShowSavedAtTooltip, setSavedAtTooltipPos)
}

// Helper function to create resize handler
function createResizeHandler(
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  return () => hideSavedAtTooltip(setShowSavedAtTooltip, setSavedAtTooltipPos)
}

// Helper function to create click outside handler
function createClickOutsideHandler(
  savedAtRef: React.RefObject<HTMLSpanElement>,
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  return (e: MouseEvent) => {
    if (isClickOutsideSavedAtTooltip(e, savedAtRef)) {
      hideSavedAtTooltip(setShowSavedAtTooltip, setSavedAtTooltipPos)
    }
  }
}

// Helper function to add savedAt tooltip listeners
function addSavedAtTooltipListeners(
  handleScroll: () => void,
  handleBlur: () => void,
  handleResize: () => void,
  handleClickOutside: (e: MouseEvent) => void
) {
  window.addEventListener('scroll', handleScroll, true)
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('blur', handleBlur)
  window.addEventListener('resize', handleResize)
}

// Helper function to remove savedAt tooltip listeners
function removeSavedAtTooltipListeners(
  handleScroll: () => void,
  handleBlur: () => void,
  handleResize: () => void,
  handleClickOutside: (e: MouseEvent) => void
) {
  window.removeEventListener('scroll', handleScroll, true)
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('blur', handleBlur)
  window.removeEventListener('resize', handleResize)
}

// Helper function to setup savedAt tooltip listeners
export function setupSavedAtTooltipListeners(
  savedAtRef: React.RefObject<HTMLSpanElement>,
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
) {
  const handleScroll = createScrollHandler(setShowSavedAtTooltip, setSavedAtTooltipPos)
  const handleBlur = createBlurHandler(setShowSavedAtTooltip, setSavedAtTooltipPos)
  const handleResize = createResizeHandler(setShowSavedAtTooltip, setSavedAtTooltipPos)
  const handleClickOutside = createClickOutsideHandler(savedAtRef, setShowSavedAtTooltip, setSavedAtTooltipPos)

  addSavedAtTooltipListeners(handleScroll, handleBlur, handleResize, handleClickOutside)

  return () => {
    removeSavedAtTooltipListeners(handleScroll, handleBlur, handleResize, handleClickOutside)
  }
}
